/**
 * Step 3: Payment - Stripe Checkout Redirect
 * Redirects to Stripe's hosted checkout page
 */

import React, { useState, useEffect } from 'react';
import { Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCreditCard,
  faCheckCircle,
  faShieldAlt,
  faLock,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { SignupData } from '../AgentSignup';
import { useSearchParams } from 'react-router-dom';
import axiosInstance from '../../../helpers/api';
import agentPortalService from '../../../services/agentPortal.service';

interface StepThreeProps {
  data: SignupData;
  updateData: (data: Partial<SignupData>) => void;
  onComplete: () => void;
  onPrevious: () => void;
}

const StepThree: React.FC<StepThreeProps> = ({ data, updateData, onComplete, onPrevious }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  // Final ladder (Aug 2026): agent-platform $300/$250 · agent-website $500/$350 TOTALS
  const [selectedPlan, setSelectedPlan] = useState<'agent-platform' | 'agent-website'>('agent-platform');
  const [promoCode, setPromoCode] = useState('');
  const withSite = selectedPlan === 'agent-website';
  // FOUNDING150: $150 activation + $150/mo for life (agent-platform only,
  // validated server-side; this flag only drives the price display)
  const isFounders = !withSite && promoCode.trim().toUpperCase() === 'FOUNDING150';

  // Check if returning from Stripe checkout
  useEffect(() => {
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');

    if (success === 'true' && sessionId) {
      verifyPayment(sessionId);
    } else if (canceled === 'true') {
      setError('Payment was canceled. You can try again when ready.');
    }
  }, [searchParams]);

  const verifyPayment = async (sessionId: string) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/portal-stripe/session/${sessionId}`);

      if (response.data.success && (response.data.status === 'paid' || response.data.status === 'complete' || response.data.status === 'no_payment_required')) {
        // Payment successful - now create the account
        console.log('✅ Payment verified, creating account...');

        // Restore signup data from localStorage (saved before Stripe redirect)
        let savedSignupData: any = {};
        try {
          const saved = localStorage.getItem('agentSignupData');
          if (saved) savedSignupData = JSON.parse(saved);
        } catch (e) {}
        const savedPlan = localStorage.getItem('agentSignupPlan') || response.data.plan || 'agent-hub';

        console.log('📋 Restored signup data:', { email: savedSignupData.email, firstName: savedSignupData.firstName, subdomain: savedSignupData.desiredSubdomain });

        try {
          await agentPortalService.signup({
            email: savedSignupData.email || response.data.customer_email || data.email || '',
            password: savedSignupData.password || data.password || 'TempPass123!',
            firstName: savedSignupData.firstName || data.firstName || '',
            lastName: savedSignupData.lastName || data.lastName || '',
            subdomain: savedSignupData.desiredSubdomain || data.desiredSubdomain || '',
            phone: savedSignupData.phone || data.phone || '',
            planId: savedPlan,
            stripeSessionId: sessionId,
            stripeSubscriptionId: response.data.subscription_id || '',
            brokerageName: savedSignupData.brokerageName || data.brokerageName || '',
            licenseNumber: savedSignupData.licenseNumber || data.licenseNumber || '',
            mlsId: savedSignupData.mlsId || data.mlsId || ''
          } as any);
          console.log('✅ Account created successfully');
          localStorage.removeItem('agentSignupData');
          localStorage.removeItem('agentSignupPlan');
        } catch (signupErr: any) {
          // Account might already exist (retry) - that's ok
          if (signupErr.response?.status !== 409) {
            console.warn('⚠️ Account creation issue:', signupErr.response?.data?.message);
          }
        }

        setPaymentSuccess(true);
        setTimeout(() => {
          onComplete();
        }, 3000);
      } else {
        setError('Payment verification pending. Please wait a moment and refresh.');
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      setError('Could not verify payment. Please contact support@kairosiq.ai');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (plan: string) => {
    try {
      setLoading(true);
      setError(null);

      // Save signup data before Stripe redirect (page will reload)
      localStorage.setItem('agentSignupData', JSON.stringify(data));
      localStorage.setItem('agentSignupPlan', plan);

      const isSponsorInvite = !!(searchParams.get('token') || searchParams.get('sponsor') || searchParams.get('invite_token'));
      const response = await axiosInstance.post('/portal-stripe/create-checkout', {
        plan,
        email: data.email,
        name: `${data.firstName} ${data.lastName}`,
        trial: isSponsorInvite,
        // Both plans are all-inclusive — classification and MLS data are in
        // the plan itself; no add-on line items.
        websiteAddon: false,
        aiOnboarding: false,
        mlsActivation: false,
        promoCode: promoCode.trim() || undefined,
      });

      if (response.data.success && response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        setError('Failed to create checkout session. Please try again.');
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.error || 'Failed to start payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (paymentSuccess) {
    return (
      <div className="text-center py-5">
        <FontAwesomeIcon icon={faCheckCircle} size="4x" className="text-success mb-4" />
        <h2>Payment Successful!</h2>
        <p className="text-muted mb-4">
          Welcome to AI Search Portal! Setting up your account...
        </p>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="step-title mb-2">Choose Your Plan</h2>
      <p className="step-subtitle mb-4">
        Select a plan to get started. You'll be redirected to our secure payment page.
      </p>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Plan choice — Platform vs Platform + Own Website */}
      <div
        style={{
          border: !withSite ? '2px solid #C9952A' : '2px solid #e2e8f0',
          borderRadius: '12px',
          padding: '24px',
          background: 'white',
          boxShadow: !withSite ? '0 0 15px rgba(201,149,42,0.15)' : 'none',
          marginBottom: '16px',
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onClick={() => setSelectedPlan('agent-platform')}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <h5 className="mb-0 fw-bold">AI Operations Platform</h5>
              <span style={{ background: '#C9952A', color: 'white', fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '3px' }}>EVERYTHING INCLUDED</span>
            </div>
            <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>Past-client monitoring, daily call lists, drafted outreach with approval queue, monthly CMAs — MLS data included in covered markets</p>
          </div>
          <div className="text-end">
            <div className="fw-bold fs-3" style={{ color: '#0F1E35' }}>$250<small className="text-muted fw-normal">/mo</small></div>
            <div style={{ fontSize: '11px', color: '#888' }}>+ $300 setup &middot; live in 7 days</div>
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2" style={{ fontSize: '12px', color: '#555' }}>
          {['Past Client Monitoring', 'Book of Business Report', 'Daily Call Lists', 'Drafted Outreach + Approval Queue', 'Monthly CMAs to Your Sphere', 'Client Hub CRM', 'AI Lead Coach', 'Neighborhood Intelligence'].map((f, i) => (
            <span key={i} style={{ background: '#f8f9fa', padding: '3px 10px', borderRadius: '4px' }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#C9952A', marginRight: '4px', fontSize: '10px' }} />{f}
            </span>
          ))}
        </div>
      </div>

      <div
        style={{
          border: withSite ? '2px solid #C9952A' : '2px solid #e2e8f0',
          borderRadius: '12px',
          padding: '24px',
          background: withSite ? '#FFFAF0' : 'white',
          boxShadow: withSite ? '0 0 15px rgba(201,149,42,0.15)' : 'none',
          marginBottom: '16px',
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onClick={() => setSelectedPlan('agent-website')}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <h5 className="mb-0 fw-bold">Platform + Your Own AI Website</h5>
              <span style={{ background: '#0F1E35', color: '#E8B44A', fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '3px' }}>YOUR DOMAIN</span>
            </div>
            <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>Everything in the platform, plus AI conversational search on your own domain — white-labeled, SEO-ready, your leads</p>
          </div>
          <div className="text-end">
            <div className="fw-bold fs-3" style={{ color: '#0F1E35' }}>$350<small className="text-muted fw-normal">/mo</small></div>
            <div style={{ fontSize: '11px', color: '#888' }}>+ $500 setup total &middot; live in 14 days</div>
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2" style={{ fontSize: '12px', color: '#555' }}>
          {['Everything in the Platform', 'Your Own AI Website', 'Custom Domain & SSL', 'AI Conversational Search', 'Full White-Label Branding', 'SEO-Optimized Pages', 'Lead Capture & Mobile'].map((f, i) => (
            <span key={i} style={{ background: withSite ? '#fff' : '#f8f9fa', padding: '3px 10px', borderRadius: '4px' }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#C9952A', marginRight: '4px', fontSize: '10px' }} />{f}
            </span>
          ))}
        </div>
      </div>

      {/* AI Onboarding Add-On */}
      <div
        style={{
          border: '2px solid #C9952A',
          borderRadius: '12px',
          padding: '20px',
          background: '#FFFAF0',
          transition: 'all 0.2s',
          marginBottom: '16px',
        }}

      >
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <div style={{
              width: '24px', height: '24px', borderRadius: '6px',
              border: '2px solid #C9952A',
              background: '#C9952A',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'white', fontSize: '14px' }} />
            </div>
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <h6 className="mb-0 fw-bold">AI Book of Business Classification</h6>
                <span style={{ background: '#e8f5e9', color: '#2e7d32', fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '3px' }}>INCLUDED</span>
              </div>
              <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>Connect your CRM and we'll organize your contacts — classify every lead, draft re-engagement emails, and build your pipeline before you start.</p>
            </div>
          </div>
          <div className="text-end" style={{ flexShrink: 0 }}>
            <div className="fw-bold" style={{ color: '#2e7d32', fontSize: '15px' }}>Included</div>
            <div style={{ fontSize: '11px', color: '#888' }}>in your setup</div>
          </div>
        </div>
        <div className="mt-3 pt-3" style={{ borderTop: '1px solid #eee' }}>
          <div className="d-flex flex-wrap gap-2" style={{ fontSize: '11px', color: '#555' }}>
            {['Full CRM Import', 'Every Lead Classified', 'Re-Engagement Drafts', 'Past Client CMA Setup', 'Industry Contacts Filtered', 'Distressed Seller Alerts'].map((f, i) => (
              <span key={i} style={{ background: '#fff', padding: '2px 8px', borderRadius: '4px', border: '1px solid #eee' }}>
                <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#C9952A', marginRight: '3px', fontSize: '9px' }} />{f}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Teams pointer */}
      <p className="text-muted mb-3" style={{ fontSize: '12px' }}>
        Building a team? <strong>$500/mo covers 3 full seats + a shared team website</strong> ($500 setup, +$100/mo per additional seat) — email{' '}
        <a href="mailto:michael@kairosiq.ai">michael@kairosiq.ai</a> and we&apos;ll set it up.
      </p>

      {/* Promo code */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <input
          type="text"
          value={promoCode}
          onChange={(e) => setPromoCode(e.target.value)}
          placeholder="Have a code?"
          style={{
            border: isFounders ? '2px solid #C9952A' : '1px solid #e2e8f0',
            borderRadius: '8px', padding: '8px 12px', fontSize: '13px', width: '180px',
          }}
        />
        {isFounders && (
          <span style={{ color: '#2e7d32', fontSize: '12px', fontWeight: 700 }}>
            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
            Founders rate applied — $150 activation + $150/mo, locked for life
          </span>
        )}
        {!isFounders && withSite && promoCode.trim() !== '' && (
          <span style={{ color: '#888', fontSize: '12px' }}>Codes apply to the AI Operations Platform plan</span>
        )}
      </div>

      {/* Total + CTA */}
      <div style={{
        background: '#0F1E35', borderRadius: '12px', padding: '20px', marginBottom: '16px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div>
          <div style={{ color: '#8FA3BF', fontSize: '12px', marginBottom: '4px' }}>Due Today</div>
          <div style={{ color: 'white', fontSize: '28px', fontWeight: 700 }}>
            ${withSite ? 500 : isFounders ? 150 : 300}
            {isFounders && <small style={{ fontSize: '13px', color: '#C9952A', fontWeight: 600, marginLeft: '8px' }}>founders activation</small>}
          </div>
          <div style={{ color: '#C9952A', fontSize: '12px', fontWeight: 600 }}>
            Then ${withSite ? 350 : isFounders ? 150 : 250}/mo — starting when you go live in {withSite ? 14 : 7} days
          </div>
          <div style={{ color: '#8FA3BF', fontSize: '10px', marginTop: '4px' }}>
            Setup includes training, your Book of Business Report loaded in, and a live group coaching session{withSite ? ' — plus your AI website deployed on your domain' : ''}
          </div>
        </div>
        <Button
          onClick={() => !loading && handleCheckout(selectedPlan)}
          disabled={loading}
          style={{
            background: 'linear-gradient(135deg, #C9952A, #E8B44A)',
            border: 'none', padding: '14px 28px', fontSize: '15px',
            fontWeight: 700, color: '#0F1E35', borderRadius: '8px',
          }}
        >
          {loading ? <Spinner size="sm" /> : <>Get Started <FontAwesomeIcon icon={faArrowRight} className="ms-2" /></>}
        </Button>
      </div>

      {/* Security badges */}
      <div className="text-center mb-4">
        <div className="d-flex justify-content-center gap-4" style={{ fontSize: '0.85rem', color: '#64748b' }}>
          <span><FontAwesomeIcon icon={faLock} className="me-1" /> SSL Encrypted</span>
          <span><FontAwesomeIcon icon={faShieldAlt} className="me-1" /> Powered by Stripe</span>
          <span><FontAwesomeIcon icon={faCreditCard} className="me-1" /> Cancel Anytime</span>
        </div>
      </div>

      {/* Back button */}
      <div className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={onPrevious} disabled={loading}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back
        </Button>
        <small className="text-muted align-self-center">
          ${withSite ? 500 : isFounders ? 150 : 300} setup today &middot; live in {withSite ? 14 : 7} days &middot; then ${withSite ? 350 : isFounders ? 150 : 250}/mo. Month-to-month, cancel anytime.
        </small>
      </div>
    </div>
  );
};

export default StepThree;
